import React, { useState, useEffect, useRef, useMemo } from "react";
import { Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import "./layout.css";
import "../assets/css/custom.css";
import {
  FaAngleDown,
  FaArrowLeft,
  FaBars,
  FaChevronLeft,
  FaChevronRight,
  FaUser,
  FaTachometerAlt,
  FaPowerOff,
  FaUserShield,
  FaTags,
  FaUsers,
  FaGift,
  FaEnvelope,
  FaRegCalendarAlt,
  FaHotel,
  FaChair,
  FaBed,
  FaHistory,
  FaCogs,
  FaChartBar,
} from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { getAllEvents } from "../utils/getAllEventMasterDetails";
import { logout, patchSelectedEvent, clearSelectedEvent } from "../redux/slices/authSlice";
import { clearAuthToken, getAuthToken } from "../utils/authToken";
import {
  canAccessModule,
  canAccessAny,
  isSuperAdminUser,
  resolveEventAccess,
} from "../utils/eventAccessModules";
import { FRONTEND_URL, getEventAccess } from "../server/Api";
import axios from "axios";

const festivaLogo = require("../assets/images/festiva_logo.svg").default;
const Layout = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(min-width: 992px)").matches
      : true
  );
  const [isMobileNav, setIsMobileNav] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 991.98px)").matches
      : false
  );
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [activeMenu, setActiveMenu] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const selectedEvent = useSelector((state) => state.auth.selectedEvent);
  const authEvents = useSelector((state) => state.auth.events);

  const [message, setMessage] = useState(null);
  const [events, setEvents] = useState([]);
  const [avatarFailed, setAvatarFailed] = useState(false);
  const profileRef = useRef(null);

  const selectedEventDetails = events.find(
    (event) => String(event._id) === String(selectedEvent?.eventId)
  );

  const accessEvent = useMemo(
    () => resolveEventAccess(selectedEvent, authEvents),
    [selectedEvent, authEvents]
  );

  const isSuperAdmin = isSuperAdminUser(user);
  const allow = (moduleKey) => canAccessModule(accessEvent, moduleKey, user);
  const allowAny = (...keys) => canAccessAny(accessEvent, keys, user);
  const showEventMenus = Boolean(selectedEvent) || !isSuperAdmin;

  const guestLinks = [
    allow("guest-master") && {
      key: "guest-master",
      to: "/guest-master",
      label: "Add Guest",
    },
    allow("guest-attendee") && {
      key: "guest-attendee",
      to: "/guest-attendee",
      label: "Add Attendee",
    },
    allow("allocate-guest") && {
      key: "allocate-guest",
      to: "/allocate-guest",
      label: "Allocate Guests",
    },
  ].filter(Boolean);

  const inviteLinks = [
    allow("send-invites") && {
      key: "send-invites",
      to: "/send-invites",
      label: "Send Invites",
    },
    allow("track-invites") && {
      key: "track-invites",
      to: "/track-invites",
      label: "RSVP",
    },
  ].filter(Boolean);

  // Super Admin always sees the full Reports catalog inside an event
  const reportLinks = [
    (isSuperAdmin || allow("reports-guest-invites")) && {
      key: "reports-guest-invites",
      to: "/reports/guest-invites",
      label: "Guest & Invites",
    },
    (isSuperAdmin || allow("stay-reports")) && {
      key: "stay-reports",
      to: "/stay-reports",
      label: "Accommodation",
    },
    (isSuperAdmin || allow("reports-travel")) && {
      key: "reports-travel",
      to: "/reports/travel",
      label: "Travel",
    },
    (isSuperAdmin || allow("reports-seat")) && {
      key: "reports-seat",
      to: "/reports/seat-allocation",
      label: "Seat Allocation",
    },
  ].filter(Boolean);

  const renderNavLink = (to, menuKey, label, Icon) => (
    <Link
      to={to}
      onClick={() => setActiveMenu(menuKey)}
      className={`list-group-item list-group-item-action bg-custom ${
        activeMenu === menuKey ? "active" : ""
      }`}
      title={label}
    >
      <Icon className="nav-icon" />
      <span className="nav-label">{label}</span>
    </Link>
  );

  const dashboardPath =
    isSuperAdmin && !selectedEvent
      ? "/super-admin-dashboard"
      : "/dashboard";

  const renderSidebarToggle = () => (
    <button
      type="button"
      className="sidebar-toggle-btn sidebar-toggle-rail"
      onClick={handleToggleSidebar}
      title={isSidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
      aria-label={isSidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
      aria-expanded={isSidebarOpen}
    >
      {isSidebarOpen ? (
        <FaChevronLeft aria-hidden="true" />
      ) : (
        <FaChevronRight aria-hidden="true" />
      )}
    </button>
  );

  useEffect(() => {
    if (isSuperAdmin || !selectedEvent?.eventId) return undefined;
    let cancelled = false;
    const loadAccess = async () => {
      try {
        const res = await axios.get(getEventAccess(selectedEvent.eventId));
        const pages = res.data?.data?.allowedPages;
        if (!cancelled && Array.isArray(pages)) {
          dispatch(
            patchSelectedEvent({
              eventId: selectedEvent.eventId,
              allowedPages: pages,
              ...(Array.isArray(res.data?.data?.pagePermissions)
                ? { pagePermissions: res.data.data.pagePermissions }
                : {}),
              ...(res.data?.data?.eventAllowedPages
                ? { eventAllowedPages: res.data.data.eventAllowedPages }
                : {}),
              ...(res.data?.data?.role ? { role: res.data.data.role } : {}),
              ...(typeof res.data?.data?.isAdmin === "boolean"
                ? { isAdmin: res.data.data.isAdmin }
                : {}),
            })
          );
        }
      } catch (error) {
        console.error("Failed to refresh event page access:", error);
      }
    };
    loadAccess();
    return () => {
      cancelled = true;
    };
  }, [isSuperAdmin, selectedEvent?.eventId, dispatch]);

  const getUserInitials = (name) => {
    const parts = String(name || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    if (!parts.length) return "U";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  };

  const profileImage = user?.profilePic || "";

  useEffect(() => {
    setAvatarFailed(false);
  }, [profileImage]);

  useEffect(() => {
    if (activeDropdown !== "profile") return undefined;
    const closeProfileMenu = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("mousedown", closeProfileMenu);
    return () => document.removeEventListener("mousedown", closeProfileMenu);
  }, [activeDropdown]);

  const handleToggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia("(max-width: 991.98px)");
    const apply = (matches) => {
      setIsMobileNav(matches);
      setIsSidebarOpen(!matches);
    };
    apply(mq.matches);
    const onChange = (e) => apply(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  useEffect(() => {
    if (isMobileNav) setIsSidebarOpen(false);
  }, [location.pathname, isMobileNav]);

  useEffect(() => {
    if (!isMobileNav || !isSidebarOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isMobileNav, isSidebarOpen]);

  const handleToggleDropdown = (dropdownId) => {
    setActiveDropdown((prev) => (prev === dropdownId ? null : dropdownId));
  };

  // Keep opened Guest Master / Reports / Invites panels fully in view
  useEffect(() => {
    if (!activeDropdown || activeDropdown === "profile") return undefined;
    const timer = window.setTimeout(() => {
      const panel = document.querySelector(
        `#sidebar .dropdown-container.show[data-dropdown="${activeDropdown}"]`
      );
      if (!panel) return;
      panel.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }, 60);
    return () => window.clearTimeout(timer);
  }, [activeDropdown]);

  const handleLogout = (e) => {
    e.stopPropagation();
    clearAuthToken();
    dispatch(logout());
    navigate("/login", { replace: true });
  };

  const handleExitEvent = () => {
    dispatch(clearSelectedEvent());
    setActiveDropdown(null);
    navigate("/event-master", { replace: true });
  };

  const fetchEvents = async () => {
    try {
      const data = await getAllEvents();
      console.log("Data received:", data);
      setEvents(data); // adjust if it's data.data
    } catch (error) {
      console.error("Error fetching events:", error);
      setMessage({ text: "Error fetching events", type: "error" });
    }
  };

  useEffect(() => {
    if (!user || !getAuthToken()) {
      clearAuthToken();
      dispatch(logout());
      navigate("/login", { replace: true });
      return;
    }

    fetchEvents();
  }, [user]);

  // ✅ Set active menu on initial load and whenever URL changes
  useEffect(() => {
    const path = location.pathname;
    let menu = "";
    let dropdown = null;

    if (path.includes("/super-admin-dashboard")) {
      menu = "dashboard";
    } else if (path.includes("/dashboard")) {
      menu = "dashboard";
    } else if (path.includes("/tecogis-admin")) {
      menu = "tecogis-admin";
    } else if (path.includes("/event-master")) {
      menu = "event-master";
    } else if (path.includes("/role-access")) {
      menu = "role-access";
    } else if (path.includes("/audit-log")) {
      menu = "audit-log";
    } else if (path.includes("/occasion-master") || path.includes("/occasion-print")) {
      menu = "occasion-master";
      dropdown = "occasion";
    } else if (path.includes("/guest-master")) {
      menu = "guest-master";
      dropdown = "guest";
    } else if (path.includes("/guest-attendee")) {
      menu = "guest-attendee";
      dropdown = "guest";
    } else if (path.includes("/allocate-guest")) {
      menu = "allocate-guest";
      dropdown = "guest";
    } else if (path.includes("/send-invites")) {
      menu = "send-invites";
      dropdown = "rsvp";
    } else if (path.includes("/track-invites")) {
      menu = "track-invites";
      dropdown = "rsvp";
    } else if (path.includes("/seat-allocation/layouts")) {
      menu = "seat-allocation-layouts";
    } else if (path.includes("/seat-allocation")) {
      menu = "seat-allocation";
    } else if (path.includes("/hotel-master")) {
      menu = "hotel-master";
    } else if (path.includes("/reports/guest-invites")) {
      menu = "reports-guest-invites";
      dropdown = "reports";
    } else if (path.includes("/reports/travel")) {
      menu = "reports-travel";
      dropdown = "reports";
    } else if (path.includes("/reports/seat-allocation")) {
      menu = "reports-seat";
      dropdown = "reports";
    } else if (path.includes("/stay-reports")) {
      menu = "stay-reports";
      dropdown = "reports";
    } else if (
      path.includes("/guest-accomodation") ||
      path.includes("/travel-and-stay") ||
      path.includes("/travel")
    ) {
      menu = "travel-and-stay";
    } else if (path.includes("/lookup")) {
      menu = "lookup";
    } else if (path.includes("/event-user")) {
      menu = "event-user";
    }

    setActiveMenu(menu);
    setActiveDropdown((prev) => (prev === "profile" ? prev : dropdown));
  }, [location.pathname]);

  return (
    <div
      className={`d-flex${isMobileNav ? " is-mobile-nav" : ""}${
        isMobileNav && isSidebarOpen ? " is-mobile-sidebar-open" : ""
      }`}
      id="wrapper"
    >
      {isMobileNav && isSidebarOpen ? (
        <button
          type="button"
          className="sidebar-backdrop"
          aria-label="Close navigation"
          onClick={() => setIsSidebarOpen(false)}
        />
      ) : null}

      {/* Sidebar */}
      <div
        id="sidebar"
        className={`bg-custom festiva-sidebar ${isSidebarOpen ? "open" : "closed"}`}
      >
        <div className="festiva-sidebar-aurora" aria-hidden="true" />
        <div className="festiva-sidebar-edge" aria-hidden="true" />
        <div className="list-group list-group-flush left-menu">
          {/* Super Admin platform menus stay at the top when drilling into an event */}
          {isSuperAdmin && (
            <>
              {!selectedEvent &&
                renderNavLink(
                  dashboardPath,
                  "dashboard",
                  "Dashboard",
                  FaTachometerAlt
                )}
              {renderNavLink(
                "/event-master",
                "event-master",
                "Event Master",
                FaRegCalendarAlt
              )}
              {renderNavLink(
                "/role-access",
                "role-access",
                "Role Access",
                FaUserShield
              )}
              {renderNavLink(
                "/audit-log",
                "audit-log",
                "Audit Log",
                FaHistory
              )}
              {renderNavLink("/lookup", "lookup", "Lookup", FaTags)}
              {selectedEvent ? (
                <div
                  className="sidebar-section-divider"
                  role="separator"
                  aria-label="Event menus"
                >
                  {isSidebarOpen ? <span>Event</span> : null}
                </div>
              ) : null}
            </>
          )}

          {!isSuperAdmin &&
            renderNavLink(
              dashboardPath,
              "dashboard",
              "Dashboard",
              FaTachometerAlt
            )}

          {showEventMenus && (
            <>
              {isSuperAdmin &&
                selectedEvent &&
                renderNavLink(
                  dashboardPath,
                  "dashboard",
                  "Dashboard",
                  FaTachometerAlt
                )}

              {(user?.isAdmin || isSuperAdmin) &&
                allow("occasion-master") &&
                renderNavLink(
                  "/occasion-master",
                  "occasion-master",
                  "Occasion Master",
                  FaGift
                )}

              {guestLinks.length === 1 &&
                renderNavLink(
                  guestLinks[0].to,
                  guestLinks[0].key,
                  guestLinks[0].label,
                  FaUsers
                )}

              {guestLinks.length > 1 && (
                <>
                  <div
                    className={`dropdown-btn${
                      activeDropdown === "guest" ? " active is-open" : ""
                    }${
                      [
                        "guest-master",
                        "guest-attendee",
                        "allocate-guest",
                      ].includes(activeMenu)
                        ? " has-active-child"
                        : ""
                    }`}
                    onClick={() => handleToggleDropdown("guest")}
                    title="Guest Master"
                  >
                    <FaUsers className="nav-icon" />
                    <span className="nav-label">Guest Master</span>
                    <FaAngleDown
                      className={`dropdown-icon ${
                        activeDropdown === "guest" ? "rotate" : ""
                      }`}
                    />
                  </div>
                  <div
                    className={`dropdown-container ${
                      activeDropdown === "guest" ? "show" : ""
                    }`}
                    data-dropdown="guest"
                  >
                    {guestLinks.map((link) => (
                      <Link
                        key={link.key}
                        to={link.to}
                        className={`dropdown-item${
                          activeMenu === link.key ? " active" : ""
                        }`}
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>
                </>
              )}

              {inviteLinks.length === 1 &&
                renderNavLink(
                  inviteLinks[0].to,
                  inviteLinks[0].key,
                  inviteLinks[0].label,
                  FaEnvelope
                )}

              {inviteLinks.length > 1 && (
                <>
                  <div
                    className={`dropdown-btn${
                      activeDropdown === "rsvp" ? " active is-open" : ""
                    }${
                      ["send-invites", "track-invites"].includes(activeMenu)
                        ? " has-active-child"
                        : ""
                    }`}
                    onClick={() => handleToggleDropdown("rsvp")}
                    title="Invites and RSVP"
                  >
                    <FaEnvelope className="nav-icon" />
                    <span className="nav-label">Invites and RSVP</span>
                    <FaAngleDown
                      className={`dropdown-icon ${
                        activeDropdown === "rsvp" ? "rotate" : ""
                      }`}
                    />
                  </div>
                  <div
                    className={`dropdown-container ${
                      activeDropdown === "rsvp" ? "show" : ""
                    }`}
                    data-dropdown="rsvp"
                  >
                    {inviteLinks.map((link) => (
                      <Link
                        key={link.key}
                        to={link.to}
                        className={`dropdown-item${
                          activeMenu === link.key ? " active" : ""
                        }`}
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>
                </>
              )}

              {allow("hotel-master") &&
                renderNavLink(
                  "/hotel-master",
                  "hotel-master",
                  "Hotel Master",
                  FaHotel
                )}

              {allow("travel-and-stay") &&
                renderNavLink(
                  "/travel-and-stay",
                  "travel-and-stay",
                  "Travel & Stay",
                  FaBed
                )}

              {allow("seat-allocation") &&
                renderNavLink(
                  "/seat-allocation/board",
                  "seat-allocation",
                  "Seat Allocation",
                  FaChair
                )}

              {allow("seat-allocation") &&
                renderNavLink(
                  "/seat-allocation/layouts",
                  "seat-allocation-layouts",
                  "Seating Layouts",
                  FaCogs
                )}

              {reportLinks.length === 1 &&
                renderNavLink(
                  reportLinks[0].to,
                  reportLinks[0].key,
                  reportLinks[0].label,
                  FaChartBar
                )}

              {reportLinks.length > 1 && (
                <>
                  <div
                    className={`dropdown-btn${
                      activeDropdown === "reports" ? " active is-open" : ""
                    }${
                      [
                        "reports-guest-invites",
                        "stay-reports",
                        "reports-travel",
                        "reports-seat",
                      ].includes(activeMenu)
                        ? " has-active-child"
                        : ""
                    }`}
                    onClick={() => handleToggleDropdown("reports")}
                    title="Reports"
                  >
                    <FaChartBar className="nav-icon" />
                    <span className="nav-label">Reports</span>
                    <FaAngleDown
                      className={`dropdown-icon ${
                        activeDropdown === "reports" ? "rotate" : ""
                      }`}
                    />
                  </div>
                  <div
                    className={`dropdown-container ${
                      activeDropdown === "reports" ? "show" : ""
                    }`}
                    data-dropdown="reports"
                  >
                    {reportLinks.map((link) => (
                      <Link
                        key={link.key}
                        to={link.to}
                        className={`dropdown-item${
                          activeMenu === link.key ? " active" : ""
                        }`}
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>
                </>
              )}

              {!isSuperAdmin &&
                user?.isAdmin &&
                allow("lookup") &&
                renderNavLink("/lookup", "lookup", "Lookup", FaTags)}

              {(user?.isAdmin || isSuperAdmin) &&
                allow("event-user") &&
                renderNavLink(
                  "/event-user",
                  "event-user",
                  "User Access",
                  FaUser
                )}

              {!isSuperAdmin &&
                (user?.isAdmin || selectedEvent?.isAdmin) &&
                renderNavLink(
                  "/audit-log",
                  "audit-log",
                  "Audit Log",
                  FaHistory
                )}
            </>
          )}
        </div>
        <div className="sidebar-footer">
          <button className="logout-btn" onClick={handleLogout} title="Logout">
            <FaPowerOff className="nav-icon" />
            <span className="nav-label">Logout</span>
          </button>
          <a
            className="sidebar-powered-by"
            href="https://www.tecogis.com/"
            target="_blank"
            rel="noreferrer"
            title="Tecogis"
          >
            Powered by <span>Tecogis</span>
          </a>
        </div>
        {renderSidebarToggle()}
      </div>

      {/* Page Content */}
      <div id="page-content-wrapper">
        <nav className="navbar navbar-expand-lg navbar-light top-menu nav-color festiva-header">
          <div className="festiva-header-shell container-fluid">
            <div className="festiva-header-aurora" aria-hidden="true" />
            <div className="festiva-header-line" aria-hidden="true" />

            <div className="header-bar">
              <div className="header-bar-left">
                {isMobileNav ? (
                  <button
                    type="button"
                    className="header-mobile-nav-btn"
                    onClick={handleToggleSidebar}
                    aria-label={isSidebarOpen ? "Close menu" : "Open menu"}
                    aria-expanded={isSidebarOpen}
                  >
                    <FaBars aria-hidden="true" />
                  </button>
                ) : null}
                <Link
                  to={dashboardPath}
                  className="header-brand-link"
                  title="Festiva"
                >
                  <span className="header-brand-plate">
                    <img
                      src={festivaLogo}
                      alt="Festiva"
                      className="header-brand-logo"
                    />
                  </span>
                </Link>
              </div>

              <div className="header-event logo-div">
                {(selectedEventDetails?.eventLogo ||
                  selectedEventDetails?.eventName ||
                  selectedEventDetails?.capacity) && (
                  <div className="header-event-podium">
                    <span className="header-event-sheen" aria-hidden="true" />
                    {selectedEventDetails?.eventLogo ? (
                      <span className="header-event-mark">
                        <img
                          src={`${FRONTEND_URL}/${selectedEventDetails.eventLogo}`}
                          alt={selectedEventDetails.eventName || "Event"}
                          className="header-event-logo"
                        />
                      </span>
                    ) : (
                      <span className="header-event-mark header-event-mark-fallback">
                        {(selectedEventDetails?.eventName || "E")
                          .trim()
                          .charAt(0)
                          .toUpperCase()}
                      </span>
                    )}

                    <div className="header-event-copy">
                      {selectedEventDetails?.eventName && (
                        <span
                          className="current-event-name"
                          title={selectedEventDetails.eventName}
                        >
                          {selectedEventDetails.eventName}
                        </span>
                      )}
                    </div>

                    {selectedEventDetails?.capacity != null &&
                      selectedEventDetails?.capacity !== "" && (
                        <div className="header-capacity-badge">
                          <span className="header-capacity-label">Capacity</span>
                          <strong className="current-event-capacity">
                            {selectedEventDetails.capacity}
                          </strong>
                        </div>
                      )}
                  </div>
                )}
              </div>

              <div className="header-bar-right">
                {isSuperAdmin && selectedEvent ? (
                  <button
                    type="button"
                    className="header-exit-event-btn"
                    onClick={handleExitEvent}
                    title="Back to Event Master"
                  >
                    <FaArrowLeft aria-hidden="true" />
                    <span>Back to Event Master</span>
                  </button>
                ) : null}
                <div className="navbar-profile" ref={profileRef}>
                  <button
                    type="button"
                    className={`navbar-profile-trigger${
                      activeDropdown === "profile" ? " open" : ""
                    }`}
                    onClick={() => handleToggleDropdown("profile")}
                    aria-haspopup="menu"
                    aria-expanded={activeDropdown === "profile"}
                  >
                    {profileImage && !avatarFailed ? (
                      <img
                        src={`${FRONTEND_URL}/${profileImage}`.replace(
                          /\\/g,
                          "/"
                        )}
                        alt=""
                        className="user-avatar"
                        onError={() => setAvatarFailed(true)}
                      />
                    ) : (
                      <span className="user-avatar-initials" aria-hidden="true">
                        {getUserInitials(user?.name)}
                      </span>
                    )}
                    <span className="user-identity">
                      <span className="user-name">{user?.name || "Guest"}</span>
                      {user?.role ? (
                        <span className="user-role">{user.role}</span>
                      ) : null}
                    </span>
                    <FaAngleDown
                      className={`user-menu-caret${
                        activeDropdown === "profile" ? " open" : ""
                      }`}
                      aria-hidden="true"
                    />
                  </button>

                  {activeDropdown === "profile" && (
                    <div className="profile-dropdown" role="menu">
                      <button
                        type="button"
                        className="profile-dropdown-item"
                        onClick={handleLogout}
                      >
                        <FaPowerOff />
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </nav>

        <div className="container-fluid bg-grey">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default Layout;
